import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type RegulatoryBriefStatus = 'draft' | 'published' | 'archived'
export type RegulatoryBriefCoverageState = 'full' | 'partial'
export type RegulatoryBriefLockStatus = 'unlocked' | 'locked' | 'expired'

export type AdminRegulatoryBriefListItem = {
  id: string
  frameworkId: string
  frameworkSlug: string
  frameworkName: string
  jurisdiction: string
  coverageState: RegulatoryBriefCoverageState
  status: RegulatoryBriefStatus
  needsReview: boolean
  lastDraftedAt: string | null
  updatedAt: string | null
  version: number
  editingLockedAt: string | null
  editingLockedByUserId: string | null
  lockHolderDisplayName: string | null
  lockStatus: RegulatoryBriefLockStatus
  lockExpiresAt: string | null
}

export type AdminRegulatoryBrief = AdminRegulatoryBriefListItem & {
  scopeSummary: string
  applicability: unknown
  keyObligations: unknown
  penaltyStructure: unknown
  recentAmendments: unknown
  coverageRationale: string
  crosswalk: unknown
  analystPublicNotes: string
  analystInternalNotes: string
  citationLog: unknown
  // KAN-271 detail content (versioned with the brief).
  inForceDate: string | null
  supervisoryAuthority: string
  geographicScope: string
  keyAnnexesRefs: unknown
}

export type AdminRegulatoryBriefsResponse = {
  briefs: AdminRegulatoryBriefListItem[]
}

export type AdminRegulatoryBriefResponse = {
  brief: AdminRegulatoryBrief
}

export type RegulatoryBriefLockResponse = {
  id: string
  version: number
  editing_locked_at?: string | null
  expires_at?: string | null
}

export type RegulatoryBriefSaveResponse = {
  version: number
}

export type RegulatoryBriefSavePayload = {
  expected_version: number
  scope_summary?: string
  applicability?: unknown
  key_obligations?: unknown
  penalty_structure?: unknown
  recent_amendments?: unknown
  coverage_rationale?: string
  crosswalk?: unknown
  analyst_public_notes?: string
  analyst_internal_notes?: string
  citation_log?: unknown
  needs_review?: boolean
  coverage_state?: RegulatoryBriefCoverageState
  // KAN-271 detail content.
  in_force_date?: string
  supervisory_authority?: string
  geographic_scope?: string
  key_annexes_refs?: unknown
}

const ADMIN_REGULATORY_BRIEFS_QUERY_KEY = ['admin-regulatory-briefs'] as const
const adminRegulatoryBriefQueryKey = (id: string) => [...ADMIN_REGULATORY_BRIEFS_QUERY_KEY, id] as const

export function useAdminRegulatoryBriefs() {
  return useQuery<AdminRegulatoryBriefsResponse>({
    queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY,
    queryFn: () => api.get<AdminRegulatoryBriefsResponse>('/admin/regulatory/briefs'),
    staleTime: 10_000,
  })
}

export function useAdminRegulatoryBrief(id?: string) {
  return useQuery<AdminRegulatoryBriefResponse>({
    queryKey: id ? adminRegulatoryBriefQueryKey(id) : [...ADMIN_REGULATORY_BRIEFS_QUERY_KEY, 'missing'],
    queryFn: () => api.get<AdminRegulatoryBriefResponse>(`/admin/regulatory/briefs/${id}`),
    enabled: !!id,
    staleTime: 5_000,
  })
}

export function useAcquireRegulatoryBriefLock(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<RegulatoryBriefLockResponse>(`/admin/regulatory/briefs/${id}/lock`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: adminRegulatoryBriefQueryKey(id) })
    },
  })
}

export function useHeartbeatRegulatoryBriefLock(id: string) {
  return useMutation({
    mutationFn: () => api.put<RegulatoryBriefLockResponse>(`/admin/regulatory/briefs/${id}/heartbeat`, {}),
  })
}

export function useUpdateRegulatoryBrief(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: RegulatoryBriefSavePayload) =>
      api.patch<RegulatoryBriefSaveResponse>(`/admin/regulatory/briefs/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: adminRegulatoryBriefQueryKey(id) })
    },
  })
}

export function useReleaseRegulatoryBriefLock(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<{ released: boolean; force_released: boolean }>(`/admin/regulatory/briefs/${id}/lock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: adminRegulatoryBriefQueryKey(id) })
    },
  })
}

export function useRegenerateRegulatoryBrief() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (frameworkId: string) =>
      api.post<{ jobId: string | number | null }>(
        `/admin/regulatory/briefs/${frameworkId}/regenerate`,
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY })
    },
  })
}

export function usePublishRegulatoryBrief() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, expectedVersion }: { id: string; expectedVersion: number }) =>
      api.post(`/admin/regulatory/briefs/${id}/publish`, {
        expected_version: expectedVersion,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY })
    },
  })
}

export function useDemoteRegulatoryBrief() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (frameworkId: string) =>
      api.post(`/admin/regulatory/briefs/${frameworkId}/demote`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_REGULATORY_BRIEFS_QUERY_KEY })
    },
  })
}
